<template>
  <div class="container mx-auto py-4 xl:py-2 text-black dark:text-gray-400 relative w-full px-4">
    <div class="relative mx-auto max-w-3xl">
      <BaseInput
          id="baseSearch"
          name="baseSearch"
          v-model="searchQuery"
          @input="handleSearchInput"
          @focus="showSuggestions = true"
          @blur="hideSuggestions"
          @keydown.enter="performSearch"
          type="text"
          label="Поиск вакансий, резюме, услуг..."
          placeholder=" "
          class="relative w-full dark:border-gray-700 rounded-md focus:ring-indigo-500 focus:border-indigo-500 dark:bg-gray-700 dark:text-gray-300"
      />
      <button
          @click="performSearch"
          class="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-blue-500"
      >
        <SearchSvg/>
      </button>
    </div>

    <div class="relative w-full mx-auto max-w-3xl">
      <div
          v-if="showSuggestions && suggestions?.length > 0"
          class="absolute z-10 mt-1 w-full bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 max-h-96 overflow-y-auto"
      >
        <div
            v-for="(suggestion, index) in suggestions"
            :key="index"
            @mousedown="selectSuggestion(suggestion)"
            class="px-4 py-2 text-black dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 cursor-pointer flex items-center"
        >
          <div class="mr-3 h-5 w-5 text-blue-500">
            <span v-html="getSuggestionIcon(suggestion.type).template"></span>
          </div>
          <div>
            <div class="font-medium">{{ suggestion.title }}</div>
            <div class="text-sm text-gray-500 dark:text-gray-400">
              {{
                suggestion.type === 'vacancy'
                    ? 'Вакансия' : suggestion.type === 'resume'
                        ? 'Резюме' : suggestion.type === 'product'
                        ? 'Товар' : 'Услуга'
              }}
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import {debounce} from 'lodash-es';
import {useSearchStore} from "@/modules/search/store/searchStore.js";
import BaseInput from "@/core/components/material/v1/BaseInput.vue";
import SearchSvg from "@/core/components/svg_components/SearchSvg.vue";

export default {
  mounted() {
    document.addEventListener('click', this.handleClickOutside);
  },
  beforeUnmount() {
    document.removeEventListener('click', this.handleClickOutside);
  },
  data() {
    const searchStore = useSearchStore();
    return {
      searchStore,
      searchQuery: '',
      suggestions: [],
      showSuggestions: false,
      selectedCategory: 'all', // all, vacancy, resume, offering, product
    };
  },
  methods: {
    handleClickOutside(event) {
      if (!this.$el.contains(event.target)) {
        this.showSuggestions = false;
      }
    },
    handleSearchInput: debounce(function () {
      if (this.searchQuery?.length > 2) {
        this.fetchSuggestions();
      } else {
        this.suggestions = [];
      }
    }, 300),

    async fetchSuggestions() {
      try {
        const response = await this.searchStore.searchSuggestions(this.searchQuery, this.selectedCategory);
        this.suggestions = response.data;
        this.showSuggestions = true;
      } catch (error) {
        console.error('Error fetching suggestions:', error);
      }
    },

    performSearch: debounce(async function () {
      if (!this.searchQuery || !this.searchQuery?.trim()) return;

      try {
        const limit = this.$route.query.limit ? parseInt(this.$route.query.limit) : 10;
        await this.searchStore.performSearch(this.searchQuery, this.selectedCategory, {limit});
        this.showSuggestions = false;

        if (this.$route.path !== '/search' || this.$route.query.q !== this.searchQuery) {
          this.$router.push({
            path: '/search',
            query: {
              q: this.searchQuery,
              category: this.selectedCategory,
              limit: limit
            }
          });
        }
      } catch (error) {
        console.error('Ошибка при поиске:', error);
      }
    }, 300, {leading: true, trailing: false}),

    selectSuggestion(suggestion) {
      this.searchQuery = suggestion.title;
      this.showSuggestions = false;

      setTimeout(() => {
        this.$router.push({
          path: `/${suggestion.type}/${suggestion.id}`
        });
      }, 100);
    },

    hideSuggestions() {
      setTimeout(() => {
        if (document.activeElement !== this.$el.querySelector('input')) {
          this.showSuggestions = false;
        }
      }, 200);
    },
    getSuggestionIcon(type) {
      const icons = {
        vacancy: {
          template: `<svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
        </svg>`
        },
        resume: {
          template: `<svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
        </svg>`
        },
        offering: {
          template: `<svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
        </svg>`
        },
        default: {
          template: `<svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
        </svg>`
        }
      };

      return icons[type] || icons.default;
    }
  },
  components: {
    SearchSvg,
    BaseInput,
  }
};
</script>
